import type { CapturedConsoleEntry, CapturedErrorEntry } from '../types';

/**
 * The page's console and uncaught errors, heard from the moment the loader
 * runs (TOADO-910).
 *
 * These listeners used to start only when the 197 KB bundle ran, after the
 * loader had waited for the browser to go idle, so everything a page logged or
 * threw while starting up, which is where most of its errors are, never reached
 * a report: 1 widget report in 73 carried any console at all. The loader now
 * starts them at once (`startPageLogs`) and leaves them on the window, and the
 * bundle takes them over (`adoptPageLogs`) rather than wrapping the console a
 * second time.
 *
 * Fingerprint-safe, as Tier 1 is: the console is wrapped and two events are
 * listened to, and `fetch` and `XMLHttpRequest` are never touched (see
 * `collectors.ts`). The loader carries this file, so it stays small.
 */

/**
 * Ring-buffer caps. Bounded because these buffers live for the entire
 * page session on someone else's site: an unbounded console buffer on a
 * chatty single-page app is a memory leak we would be responsible for.
 */
export const MAX_CONSOLE = 200;
/**
 * Debug lines kept at most: past this the oldest debug line goes, not the
 * oldest line of any level, so a page in development mode cannot push out the
 * log, warn and error lines a report is for (TOADO-910, z.ai on the extension).
 */
const MAX_DEBUG = 50;
export const MAX_ERRORS = 50;

/** Truncation for a single console argument, before joining. */
const MAX_ARG_CHARS = 2000;
/** Truncation for the joined message. */
export const MAX_MESSAGE_CHARS = 4000;

export interface PageLogs {
  /** When listening started (epoch ms): what an empty console is empty since. */
  since: number;
  console: CapturedConsoleEntry[];
  errors: CapturedErrorEntry[];
  /**
   * Stop listening: detach both listeners, restore the original console
   * methods where nothing has wrapped them since, and record nothing more
   * through a wrapper that could not be taken off.
   */
  stop(): void;
}

/**
 * The console methods heard, and the level each is stored at. `debug` keeps
 * its own level (Vite's `[vite] connected` lines are debug); `trace` and
 * `table` print like `log`; `assert` is an error, and only when it fails.
 */
const METHODS = {
  log: 'log', info: 'info', warn: 'warn', error: 'error',
  debug: 'debug', trace: 'log', table: 'log', assert: 'error',
} as const;
type Method = keyof typeof METHODS;

/**
 * Best-effort stringification of a console argument.
 *
 * Deliberately defensive: host pages log proxies, DOM nodes, objects with
 * throwing getters, and circular structures. A collector that throws while
 * formatting a log line would break the page it is supposed to be
 * observing, which is the worst possible failure for an embedded script.
 */
export function stringifyArg(arg: unknown): string {
  try {
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';
    const type = typeof arg;
    if (type === 'string') return (arg as string).slice(0, MAX_ARG_CHARS);
    if (type === 'number' || type === 'boolean' || type === 'bigint') return String(arg);
    if (type === 'function') return `[Function ${(arg as { name?: string }).name || 'anonymous'}]`;
    if (type === 'symbol') return String(arg);
    if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
    if (typeof Node !== 'undefined' && arg instanceof Node) {
      return `[${arg.nodeName.toLowerCase()}]`;
    }
    const seen = new WeakSet<object>();
    return JSON.stringify(arg, (_key, value: unknown) => {
      if (value && typeof value === 'object') {
        if (seen.has(value as object)) return '[Circular]';
        seen.add(value as object);
      }
      return value;
    })?.slice(0, MAX_ARG_CHARS) ?? String(arg);
  } catch {
    return '[unserializable]';
  }
}

/** Push with a hard cap, dropping the oldest entry. */
function pushCapped<T>(buffer: T[], entry: T, cap: number): void {
  buffer.push(entry);
  if (buffer.length > cap) buffer.shift();
}

/** What a console call says, or null when it says nothing (an assertion that held). */
function messageOf(method: Method, args: unknown[]): string | null {
  if (method === 'assert') {
    if (args[0]) return null;
    const rest = args.slice(1).map(stringifyArg).join(' ');
    return rest ? `Assertion failed: ${rest}` : 'Assertion failed';
  }
  const joined = args.map(stringifyArg).join(' ');
  // Chrome prints a bare `console.trace()` as "console.trace".
  return method === 'trace' && !joined ? 'console.trace' : joined;
}

export function startPageLogs(): PageLogs {
  const since = Date.now();
  const consoleEntries: CapturedConsoleEntry[] = [];
  const errorEntries: CapturedErrorEntry[] = [];
  /**
   * Set by `stop()`. A wrapper that another tool has wrapped since cannot be
   * taken off the console without taking theirs off too, so it stays in the
   * chain and has to stop recording on its own (TOADO-562): otherwise a
   * Minimal widget kept collecting the page's console after it was told not
   * to.
   */
  let stopped = false;

  // ---- console -----------------------------------------------------------
  const originals: Partial<Record<Method, (...args: unknown[]) => void>> = {};
  /** Our own wrappers, so `stop()` can tell them from someone else's. */
  const installed: Partial<Record<Method, (...args: unknown[]) => void>> = {};

  for (const method of Object.keys(METHODS) as Method[]) {
    // A frozen console, or a non-writable method, is a real configuration
    // on hardened sites and in some CSP setups. Assigning to it throws —
    // and an uncaught exception from a third-party script during page load
    // is exactly the kind of damage an embedded widget must never cause.
    // So can reading it, behind a getter or a proxy (z.ai, round 4).
    let original: ((...args: unknown[]) => void) | undefined;
    let descriptor: PropertyDescriptor | undefined;
    try {
      original = console[method] as typeof original;
      descriptor = Object.getOwnPropertyDescriptor(console, method);
    } catch {
      continue;
    }
    if (typeof original !== 'function') continue;
    if (descriptor && descriptor.configurable === false && descriptor.writable === false) continue;
    const callThrough = original;

    const wrapped = function wrappedConsole(...args: unknown[]): void {
      try {
        const message = stopped ? null : messageOf(method, args);
        if (message !== null) {
          if (METHODS[method] === 'debug') {
            let debug = 0;
            for (const entry of consoleEntries) if (entry.level === 'debug') debug += 1;
            if (debug >= MAX_DEBUG) consoleEntries.splice(consoleEntries.findIndex((entry) => entry.level === 'debug'), 1);
          }
          pushCapped(consoleEntries, {
            timestamp: Date.now(),
            level: METHODS[method],
            message: message.slice(0, MAX_MESSAGE_CHARS),
          }, MAX_CONSOLE);
        }
      } catch {
        // Never let capture break the host page's logging.
      }
      // Call through with the original receiver so devtools' "source"
      // annotation and any further wrapping still behave.
      callThrough.apply(console, args);
    };

    try {
      (console as unknown as Record<Method, unknown>)[method] = wrapped;
      originals[method] = callThrough;
      installed[method] = wrapped;
    } catch {
      // Could not wrap this method. Carry on with the others rather than
      // failing the whole widget over one property.
    }
  }

  // ---- uncaught errors ---------------------------------------------------
  /**
   * Identical errors repeat — a broken render loop can fire thousands of
   * times a second. Collapse by message+stack and count, so the buffer
   * holds distinct problems rather than one problem 50 times.
   */
  const recordError = (rawMessage: string, rawStack: string | undefined, source: string | undefined): void => {
    if (stopped) return;
    // Truncate BEFORE comparing. Comparing raw strings while storing
    // truncated ones meant any error longer than the cap — routine for deep
    // async stacks — never matched an existing entry, so the repeat
    // collapsing silently switched off exactly when a render loop needed
    // it and churned the whole buffer.
    const message = rawMessage.slice(0, MAX_MESSAGE_CHARS);
    const stack = rawStack?.slice(0, MAX_MESSAGE_CHARS);
    const existing = errorEntries.find((e) => e.message === message && e.stack === stack);
    if (existing) {
      existing.count += 1;
      existing.timestamp = Date.now();
      return;
    }
    pushCapped(errorEntries, { timestamp: Date.now(), message, stack, source, count: 1 }, MAX_ERRORS);
  };

  const onError = (event: ErrorEvent): void => {
    try {
      recordError(
        event.message || String(event.error ?? 'Unknown error'),
        event.error instanceof Error ? event.error.stack : undefined,
        event.filename ? `${event.filename}:${event.lineno ?? 0}` : undefined,
      );
    } catch { /* ignore */ }
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    try {
      const reason = event.reason as unknown;
      recordError(
        reason instanceof Error ? `Unhandled rejection: ${reason.message}` : `Unhandled rejection: ${stringifyArg(reason)}`,
        reason instanceof Error ? reason.stack : undefined,
        undefined,
      );
    } catch { /* ignore */ }
  };

  window.addEventListener('error', onError, true);
  window.addEventListener('unhandledrejection', onRejection, true);

  return {
    since,
    console: consoleEntries,
    errors: errorEntries,
    stop(): void {
      if (stopped) return;
      stopped = true;
      for (const method of Object.keys(METHODS) as Method[]) {
        const original = originals[method];
        if (!original) continue;
        // Restore ONLY if our wrapper is still the current method. If an
        // error-monitoring tool wrapped console after us, blindly writing
        // the pre-Toado function back would silently uninstall THEIR
        // instrumentation — breaking a customer's observability because
        // our widget was torn down.
        if (console[method] !== installed[method]) continue;
        try {
          (console as unknown as Record<Method, unknown>)[method] = original;
        } catch {
          // Property became read-only after we wrapped it; nothing to do.
        }
      }
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection, true);
    },
  };
}

/** Where the loader leaves its listeners for the bundle. */
export const PAGE_LOGS_HANDOFF = '__toadoPageLogs';
const HANDOFF = PAGE_LOGS_HANDOFF;

/**
 * The loader's side: start listening and leave the listeners on the window.
 * Non-enumerable, and configurable so the bundle can take them off it; the
 * handle is frozen, so its start time and its stop stay the ones it was made
 * with (its buffers, which the listeners fill, cannot be).
 */
export function leavePageLogs(): PageLogs | null {
  // The loader calls this after claiming its guard; startPageLogs guards every
  // read and write of the page's console, so nothing here throws out of it.
  const logs = Object.freeze(startPageLogs());
  try {
    Object.defineProperty(window, HANDOFF, { value: logs, configurable: true, enumerable: false, writable: false });
    return logs;
  } catch {
    // A window that will not take the property: nothing could adopt these.
    logs.stop();
    return null;
  }
}

/**
 * Whether working listeners are already on the window, the page's own early
 * start's or another loader's. Anything else at that name (`undefined`, `{}`,
 * a broken handle) is taken off and stopped where it can be, so the loader
 * starts its own rather than trusting it and hearing nothing (Codex, round 3).
 */
export function pageLogsLeft(): boolean {
  try {
    if (!(HANDOFF in window)) return false;
    if (handoffParts((window as unknown as Record<string, unknown>)[HANDOFF])) return true;
  } catch { /* fall through: taken off below */ }
  adoptPageLogs()?.stop();
  return false;
}

/** The parts of a handle of the shape the loader leaves, or null. Reads each once; may throw on a getter. */
function handoffParts(found: unknown): (Omit<PageLogs, 'stop'> & { stop: () => void }) | null {
  if (!found || typeof found !== 'object') return null;
  const { since, console: consoleEntries, errors, stop } = found as Partial<PageLogs>;
  const valid = typeof since === 'number' && Number.isFinite(since) && since <= Date.now()
    && Array.isArray(consoleEntries) && consoleEntries.length <= MAX_CONSOLE
    && Array.isArray(errors) && errors.length <= MAX_ERRORS
    && typeof stop === 'function';
  return valid ? { since, console: consoleEntries, errors, stop } : null;
}

/**
 * Stop whatever listeners are on the window and take them off it: the loader's
 * when its bundle failed to load, or the Toado app's own early start when the
 * session turns out not to be staff. Nothing will take them over.
 */
export function dropPageLogs(): void {
  adoptPageLogs()?.stop();
}

/**
 * The bundle's side: take the loader's listeners off the window, so nothing
 * else can, or null when there are none to take.
 *
 * The page can put anything at that name (Codex, round 1, TOADO-910): what is
 * there is read once, inside a try, so a getter that throws costs only the
 * handoff; anything not of the shape the loader leaves is stopped, if it can
 * be, and the bundle starts its own; and what is returned is a handle of this
 * script's own, whose stop cannot throw into the widget's teardown. The
 * entries in the buffers are the page's to write either way, as the console
 * itself is, so a report reads them strictly (`readableConsole` and
 * `readableErrors`, Codex round 3: an entry whose message was an object made
 * the report's redaction throw).
 */
export function adoptPageLogs(): PageLogs | null {
  let stop: unknown;
  let found: unknown;
  try {
    if (!(HANDOFF in window)) return null;
    found = (window as unknown as Record<string, unknown>)[HANDOFF];
    delete (window as unknown as Record<string, unknown>)[HANDOFF];
    stop = found && typeof found === 'object' ? (found as Partial<PageLogs>).stop : undefined;
    const parts = handoffParts(found);
    if (parts) {
      const stopFound = parts.stop;
      return {
        since: parts.since, console: parts.console, errors: parts.errors,
        stop: () => { try { stopFound.call(found); } catch { /* not ours to fix */ } },
      };
    }
  } catch { /* fall through: stopped below where it can be */ }
  try { if (typeof stop === 'function') (stop as () => void).call(found); } catch { /* nothing more to do */ }
  return null;
}

const LEVELS: ReadonlySet<string> = new Set(['log', 'info', 'warn', 'error', 'debug']);

/** The console entries a report can send: of the shape the listeners write, and no more of them than they keep. */
export function readableConsole(entries: readonly unknown[]): CapturedConsoleEntry[] {
  const kept: CapturedConsoleEntry[] = [];
  for (const entry of entries.slice(-MAX_CONSOLE)) {
    const e = entry as Partial<CapturedConsoleEntry> | null;
    if (e && typeof e === 'object' && typeof e.timestamp === 'number' && Number.isFinite(e.timestamp)
      && typeof e.level === 'string' && LEVELS.has(e.level) && typeof e.message === 'string') {
      kept.push({ timestamp: e.timestamp, level: e.level, message: e.message.slice(0, MAX_MESSAGE_CHARS) });
    }
  }
  return kept;
}

/** The errors a report can send, read the same way. */
export function readableErrors(entries: readonly unknown[]): CapturedErrorEntry[] {
  const kept: CapturedErrorEntry[] = [];
  for (const entry of entries.slice(-MAX_ERRORS)) {
    const e = entry as Partial<CapturedErrorEntry> | null;
    if (!e || typeof e !== 'object' || typeof e.timestamp !== 'number' || !Number.isFinite(e.timestamp)
      || typeof e.message !== 'string' || typeof e.count !== 'number' || !Number.isFinite(e.count)) continue;
    kept.push({
      timestamp: e.timestamp,
      message: e.message.slice(0, MAX_MESSAGE_CHARS),
      count: e.count,
      ...(typeof e.stack === 'string' ? { stack: e.stack.slice(0, MAX_MESSAGE_CHARS) } : {}),
      ...(typeof e.source === 'string' ? { source: e.source.slice(0, MAX_MESSAGE_CHARS) } : {}),
    });
  }
  return kept;
}
